<script setup lang="ts">
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import { computed, onBeforeMount, onMounted, ref } from 'vue'

const route = useRoute()
const app = useAppConfig()
const userStore = useUserStore()
const { t } = useI18n()
const cookieShow = ref(false)
const runtimeConfig = useRuntimeConfig()
const router = useRouter()
const cookieShowPaths = ['/login', '/register', '/forgot-password', '/']

const { cookieStatus, cookieReason } = storeToRefs(userStore)
/**
 * Global head configuration
 * @see https://unhead.harlanzw.com/
 * @see https://github.com/unjs/unhead/tree/main
 */

const siteSource = computed(() => {
  return runtimeConfig.public.source == 'user' ? 'Gigcep' : '360Sosyal'
})
const siteImage = computed(() => {
  return runtimeConfig.public.source == 'user'
    ? 'gig-logo.png'
    : '360-sosyal-logo.svg'
})

useHead({
  title: route.meta.title,
  titleTemplate: (titleChunk) => {
    return titleChunk
      ? `${t(titleChunk)} - ${
          runtimeConfig.public.source == 'user' ? t('gigcep') : t('sosyal360')
        }`
      : `${
          runtimeConfig.public.source == 'user' ? t('gigcep') : t('sosyal360')
        }`
  },
  meta: [
    {
      name: 'color-scheme',
      content: 'light only',
    },
    {
      name: 'description',
      content: `Sosyal medyada suni ve sahte çözümler değil, gerçek hesaplarla zirveyi zorla. ${siteSource.value} yeni dünyanın kazanç kapılarını senin için aralıyor.`,
    },
    {
      name: 'keywords',
      content: `instagram beğeni, instagram takipçi, facebook beğeni, facebook takipçi, sosyal medya, youtube yorum, youtube izlenme, sosyal medya beğeni, sosyal medya yorum, dijital çözümler, organik çözümler, ${siteSource.value}`,
    },
    {
      name: 'author',
      content: siteSource.value,
    },
    //Open Graph
    {
      property: 'og:title',
      content: `${siteSource.value} - Dijital ve Sosyal Medyada Organik Çözümler`,
    },
    {
      property: 'og:description',
      content: `Sosyal medyada suni ve sahte çözümler değil, gerçek hesaplarla zirveyi zorla. ${siteSource.value} yeni dünyanın kazanç kapılarını senin için aralıyor.`,
    },
    {
      property: 'og:image',
      content: `https://${siteSource.value.toLowerCase()}.com/img/logo/${
        siteImage.value
      }`,
    },
    {
      property: 'og:url',
      content: `https://${siteSource.value.toLowerCase()}.com`,
    },
    //Twitter
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:title',
      content: `${siteSource.value} - Dijital ve Sosyal Medyada Organik Çözümler`,
    },
    {
      name: 'twitter:description',
      content: `Sosyal medyada suni ve sahte çözümler değil, gerçek hesaplarla zirveyi zorla. ${siteSource.value} yeni dünyanın kazanç kapılarını senin için aralıyor.`,
    },
    {
      name: 'twitter:image',
      content: `https://${siteSource.value.toLowerCase()}.com/img/logo/${
        siteImage.value
      }`,
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: `https://${siteSource.value.toLowerCase()}.com`,
    },
  ],
  script:
    runtimeConfig.public.source === 'customer'
      ? [
          {
            src: 'https://code.jivosite.com/widget/7R4JYupIvc',
            async: true,
            defer: true,
          },
        ]
      : [],
})

function initGtag() {
  window.dataLayer = window.dataLayer || []
  function gtag() {
    dataLayer.push(arguments)
  }
  gtag('js', new Date())

  gtag('config', 'G-SQ6MWCFNXP')
}

function initClarity() {
  const clarityScript = document.createElement('script')
  clarityScript.async = true
  clarityScript.defer = true
  clarityScript.innerHTML = `(function(c,l,a,r,i,t,y){
    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
  })(window, document, "clarity", "script", "paqj6e390a");`
  document.head.appendChild(clarityScript)
}

onMounted(() => {
  userStore.initialize()
  if (route.path == '/' && runtimeConfig.public.source == 'user') {
    router.push('/home')
    return
  }
  if (!cookieStatus.value) {
    cookieShow.value = true
  }

  setTimeout(() => {
    initGtag()
    initClarity()
  }, 2000)
})

const cookieShowWithPath = computed(() => {
  return cookieShowPaths.includes(route.path)
})

onBeforeMount(() => {
  localStorage['nuxt-color-mode'] = 'light'
  localStorage['theme'] = 'light'
  localStorage.removeItem('theme')
  document.documentElement.classList.remove('dark')
})

function cookieAction(value: string) {
  cookieShow.value = false
  cookieStatus.value = true
  cookieReason.value = value
  useCookie('cookieStatus').value = true
  useCookie('cookieReason').value = value
}
</script>

<template>
  <div>
    <NuxtLayout :class="`${cookieShow ? '!mb-[6rem]' : ''}`">
      <NuxtLoadingIndicator color="rgb(var(--color-muted-800))" />
      <NuxtPage />
      <div
        :class="`${cookieShow && cookieShowWithPath ? 'fixed' : 'hidden'}`"
        class="bottom-0 left-0 flex h-28 w-full bg-orange-500"
        style="z-index: 60"
      >
        <div
          class="container m-auto flex max-w-4xl flex-row flex-nowrap items-center p-3"
        >
          <div
            class="border-primary-400 mr-4 cursor-pointer rounded-md border bg-red-500 px-3 py-2 text-white transition-all duration-200 hover:!text-slate-500"
            @click="cookieAction('reject')"
          >
            <Icon name="material-symbols:close" class="size-6" />
          </div>
          <p class="text-md line-clamp-3 text-white">
            {{ runtimeConfig.public.source == 'user' ? 'Gigcep' : '360Sosyal' }}
            kullanım deneyiminizi kişiselleştirmek ve iyileştirmek için çerezler
            kullanıyoruz. Ziyaretinizi gerçekleştirerek
            <NuxtLink
              target="_blank"
              :to="`${
                runtimeConfig.public.source == 'user'
                  ? '/pages/Gigcep-Gizlilik-Politikasi'
                  : '/pages/360Sosyal-Gizlilik-Politikasi'
              }`"
              class="border-b border-b-white"
            >
              <strong>Gizlilik</strong>
            </NuxtLink>
            ve
            <NuxtLink
              target="_blank"
              :to="`${
                runtimeConfig.public.source == 'user'
                  ? '/pages/Gigcep-KVKK-Politikasi'
                  : '/pages/360Sosyal-KVKK-Politikasi'
              }`"
              class="border-b border-b-white"
            >
              <strong>KVKK</strong>
            </NuxtLink>
            sözleşmemizde belirtilen maddeleri kabul etmiş olursunuz.
          </p>
        </div>
      </div>
    </NuxtLayout>
  </div>

  <Head> </Head>
</template>

<style lang="scss" scoped></style>
